import React, { useEffect, useRef, useState } from 'react';
import EmailHeader from '../GetStarted/EmailHeader';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Sparkle,
  SpellCheck,
} from 'lucide-react';
import Quill from 'quill';
import EditEmail from './EditEmail';
import EditToolbar from './EditToolbar';
import { useEmailContext } from 'features/email-generator/EmailGeneratorContext';
import { useNavigate } from 'react-router-dom';
import { Button } from 'components/ui/button';
import ExportEmail from '../modals/ExportEmail';
import InputFormMobile from '../GetStarted/InputFormMobile';

interface EmailEditorProps {
  inputPanelOpen: boolean;
}

const EmailEditor = ({ inputPanelOpen }: EmailEditorProps) => {
  const [wordCount, setWordCount] = useState(0);
  const [activeFormats, setActiveFormats] = useState<{
    [key: string]: boolean | number | string | unknown;
  }>({});
  const {
    emailContent,
    activeIndex,
    setActiveIndex,
    isPanelOpen,
    setIsPanelOpen,
  } = useEmailContext();
  const [activePanel, setActivePanel] = useState<'Edit' | 'Format'>('Edit');

  const quillRef = useRef<Quill | null>(null);
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 800);

  // Update isMobile based on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 800);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleFormatClick = (
    format: string,
    value?: number | null | string
  ) => {
    if (quillRef.current) {
      const quill = quillRef.current;
      const selection = quill.getSelection();
      if (!selection) return;

      const format_handlers: { [key: string]: () => void } = {
        bold: () => {
          quill.format('bold', !quill.getFormat(selection).bold);
        },
        italic: () => {
          quill.format('italic', !quill.getFormat(selection).italic);
        },
        underline: () => {
          quill.format('underline', !quill.getFormat(selection).underline);
        },
        strike: () => {
          quill.format('strike', !quill.getFormat(selection).strike);
        },
        link: () => {
          const url = prompt('Enter URL:');
          if (url) {
            // Ensure URL has http/https prefix
            let formattedUrl = url;
            if (!/^https?:\/\//i.test(url)) {
              formattedUrl = 'https://' + url;
            }
            quill.format('link', formattedUrl);
          } else {
            // If URL is empty or cancelled, remove the link
            quill.format('link', false);
          }
        },
        bullet: () => {
          const currentList = quill.getFormat(selection).list;
          quill.format('list', currentList === 'bullet' ? false : 'bullet');
        },
        ordered: () => {
          const currentList = quill.getFormat(selection).list;
          quill.format('list', currentList === 'ordered' ? false : 'ordered');
        },
        check: () => {
          const currentList = quill.getFormat(selection).list;
          quill.format('list', currentList === 'check' ? false : 'check');
        },
        header: () => {
          if (typeof value === 'number' || value === null) {
            quill.format('header', value);
          }
        },
        indent: () => {
          const currentIndent =
            (quill.getFormat(selection).indent as number) || 0;
          quill.format('indent', currentIndent + 1);
        },
        color: () => {
          quill.format('color', value || false);
        },
        background: () => {
          quill.format('background', value || false);
        },
        table: () => {
          const rows = prompt('Number of rows:', '3');
          const cols = prompt('Number of columns:', '3');
          if (rows && cols) {
            const numRows = parseInt(rows);
            const numCols = parseInt(cols);
            let table = '<table><tbody>';
            for (let i = 0; i < numRows; i++) {
              table += '<tr>';
              for (let j = 0; j < numCols; j++) {
                table += '<td><br></td>';
              }
              table += '</tr>';
            }
            table += '</tbody></table>';
            quill.clipboard.dangerouslyPasteHTML(
              quill.getSelection()?.index || 0,
              table
            );
          }
        },
      };

      const handler = format_handlers[format];
      if (handler) {
        handler();
        quill.history.cutoff();

        if (quill.getSelection()) {
          const selection = quill.getSelection();
          if (selection) {
            setActiveFormats(
              quill.getFormat(selection) as {
                [key: string]: boolean | number | string | unknown;
              }
            );
          }
        }
      }
    }
  };

  const handleQuillRef = (quill: Quill) => {
    quillRef.current = quill;
  };

  const handleActiveFormatsChange = (formats: {
    [key: string]: boolean | number | string | unknown;
  }) => {
    setActiveFormats(formats);
  };

  const handleWordCountChange = (count: number) => {
    setWordCount(count);
  };

  // Handle undo functionality
  const handleUndo = () => {
    if (quillRef.current) {
      const quill = quillRef.current;

      // Get current content before undo
      const currentContent = quill.root.innerHTML;

      // Perform undo
      quill.history.undo();

      // Check if content is empty after undo
      const newContent = quill.root.innerHTML;

      // If content is empty or just contains empty paragraph tags, restore the previous content
      if (
        !newContent ||
        newContent === '<p><br></p>' ||
        newContent === '<p></p>'
      ) {
        quill.root.innerHTML = currentContent;
        return;
      }

      // Update active formats after undo
      const selection = quill.getSelection();
      if (selection) {
        setActiveFormats(
          quill.getFormat(selection) as {
            [key: string]: boolean | number | string | unknown;
          }
        );
      }
    }
  };

  // Handle redo functionality
  const handleRedo = () => {
    if (quillRef.current) {
      const quill = quillRef.current;

      // Perform redo
      quill.history.redo();

      // Ensure we have a valid selection after redo
      if (!quill.getSelection()) {
        quill.setSelection(0, 0);
      }

      // Update active formats after redo
      const selection = quill.getSelection();
      if (selection) {
        setActiveFormats(
          quill.getFormat(selection) as {
            [key: string]: boolean | number | string | unknown;
          }
        );
      }
    }
  };

  // Add keyboard shortcut handlers for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if the editor has focus
      if (document.activeElement?.closest('.ql-editor')) {
        // Undo: Ctrl+Z
        if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
          e.preventDefault();
          handleUndo();
        }

        // Redo: Ctrl+Y or Ctrl+Shift+Z
        if (
          (e.ctrlKey && e.key === 'y') ||
          (e.ctrlKey && e.shiftKey && e.key === 'z')
        ) {
          e.preventDefault();
          handleRedo();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleUndo, handleRedo]);

  //   to navigate if no content
  useEffect(() => {
    if (!emailContent) {
      navigate('/email-generator/get-started');
    }
  }, [emailContent, navigate]);

  if (!emailContent) return null;

  return (
    <div
      className={`${inputPanelOpen ? 'w-[77%]' : 'w-full'} flex-grow  bg-background-main rounded-lg max-h-full relative`}
    >
      <EmailHeader />
      {/* container */}
      <div className="flex flex-col justify-center items-center pt-6">
        {/* draft no */}
        <div className="flex items-center gap-7">
          <ArrowLeft
            size={16}
            color="#846CEC"
            className="cursor-pointer"
            onClick={() => setActiveIndex((idx) => Math.max(0, idx - 1))}
          />
          <span className="font-semibold text-[14px] leading-[18px] text-text-primary">
            {activeIndex + 1} / {emailContent?.length ?? 0}
          </span>
          <ArrowRight
            size={16}
            color="#846CEC"
            className="cursor-pointer"
            onClick={() =>
              setActiveIndex((idx) =>
                Math.min(idx + 1, (emailContent?.length ?? 1) - 1)
              )
            }
          />
        </div>

        <div className="text-sm text-gray-500 mt-2 w-full flex justify-start pl-7 md:hidden">
          {wordCount} WORDS
        </div>

        {/* generated email */}
        <div className="flex-1 px-4">
          <main className={`relative h-full overflow-hidden`}>
            <div
              className={`max-w-[850px] mx-auto sm:px-4 pt-2 overflow-y-auto `}
            >
              <EditEmail
                content={emailContent[activeIndex]}
                activeIndex={activeIndex}
                onQuillRef={handleQuillRef}
                onActiveFormatsChange={handleActiveFormatsChange}
                onWordCountChange={handleWordCountChange}
              />
            </div>
          </main>
        </div>
        {/* editor tools */}
        <div className="flex-none bg-background-main rounded-lg w-full absolute bottom-0 hidden md:block">
          <EditToolbar
            onFormatClick={handleFormatClick}
            wordCount={wordCount}
            activeFormats={activeFormats}
            onUndo={handleUndo}
            onRedo={handleRedo}
          />
        </div>

        {/* for mobile */}
        <div className="flex justify-center gap-10 md:hidden bg-background-main w-screen p-[10px] fixed z-[100] bottom-0 right-[1px] shadow-2xl border-t-[1px] rounded-t-md">
          <Button
            className="w-full text-[16px]"
            variant="customSecondary"
            onClick={() => {
              setIsPanelOpen(true);
              setActivePanel('Edit');
            }}
          >
            Edit
          </Button>

          <ExportEmail />
        </div>

        {isPanelOpen && isMobile && (
          <div className="fixed bottom-0 left-0 w-full bg-background pb-5 z-[999] shadow-2xl rounded-t-md">
            <div className="flex w-full ">
              {/* Edit Tab */}
              <button
                className={`flex w-1/2 justify-center items-center gap-2 py-5 rounded-t-xl ${
                  activePanel === 'Edit' ? 'bg-primary' : 'bg-secondary'
                }`}
                onClick={() => setActivePanel('Edit')}
              >
                <span
                  className={`font-medium text-[18px] ${
                    activePanel === 'Edit' ? 'text-white' : 'text-primary'
                  }`}
                >
                  Edit
                </span>
                <Sparkle
                  size={20}
                  color={activePanel === 'Edit' ? 'white' : 'primary'}
                />
              </button>

              {/* Format Tab */}
              <button
                className={`flex w-1/2 justify-center items-center gap-2 py-5 rounded-t-xl ${
                  activePanel === 'Format' ? 'bg-primary ' : 'bg-secondary'
                }`}
                onClick={() => setActivePanel('Format')}
              >
                <span
                  className={`font-medium text-[18px] ${
                    activePanel === 'Format' ? 'text-white' : 'text-primary'
                  }`}
                >
                  Format
                </span>
                <SpellCheck
                  size={20}
                  color={activePanel === 'Format' ? 'white' : 'primary'}
                />
              </button>
            </div>

            {/* Content based on active panel */}
            <div className="">
              {activePanel === 'Edit' ? (
                <InputFormMobile />
              ) : (
                <EditToolbar
                  onFormatClick={handleFormatClick}
                  wordCount={wordCount}
                  activeFormats={activeFormats}
                  onUndo={handleUndo}
                  onRedo={handleRedo}
                />
              )}
            </div>

            {activePanel === 'Format' && (
              <Button
                variant="customPrimary"
                className="absolute right-3 bottom-5 h-[34px]"
                onClick={() => setIsPanelOpen(false)}
              >
                <Check />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailEditor;
